/**
 * pending-approvals.service.ts
 *
 * Async-Approval-Loop Block 2 — Doc Approval-Queue im practice-web.
 *
 * Wrapped die DB-RPC list_my_pending_orders aus Migration
 * 20260603000000_list_my_pending_orders.sql, plus PIN-Verify (verify_physician_approval_pin)
 * und direktes UPDATE auf visit_orders fuer status='ordered'.
 *
 * Authorization-Modell:
 *   - list: facility_physicians.user_id = auth.uid() (DB-erzwungen)
 *   - approve: visit_orders RLS + verify_physician_approval_pin RPC
 *
 * Realtime:
 *   - subscribeToMyPendingOrders ruft cb() bei jedem visit_orders-INSERT/UPDATE/DELETE
 *     auf, dass die UI list_my_pending_orders neu aufruft (cheap query, < 100ms).
 */

import type { RealtimeChannel } from "@supabase/supabase-js";

import { supabase } from "@/lib/supabase";

export type PendingOrderType = "medication" | "lab" | "imaging" | "referral" | "therapy" | "other";
export type PendingPriority = "routine" | "urgent" | "stat";

export interface PendingApproval {
    orderId: string;
    visitId: string;
    facilityId: string;
    facilityName: string | null;
    physicianId: string;
    residentId: string;
    residentFirstName: string;
    residentLastName: string;
    roomNumber: string | null;
    orderType: PendingOrderType;
    description: string;
    details: string | null;
    priority: PendingPriority;
    dueDate: string | null;
    scheduledDate: string | null;
    orderedAt: string;
    orderedBy: string | null;
    orderedByName: string | null;
}

export interface BatchApproveResult {
    approved: string[];
    failed: Array<{ orderId: string; error: string }>;
}

interface RawPendingOrderRow {
    order_id: string;
    visit_id: string;
    facility_id: string;
    facility_name: string | null;
    physician_id: string;
    resident_id: string;
    resident_first_name: string | null;
    resident_last_name: string | null;
    room_number: string | null;
    order_type: PendingOrderType;
    description: string;
    details: string | null;
    priority: PendingPriority;
    due_date: string | null;
    scheduled_date: string | null;
    ordered_at: string;
    ordered_by: string | null;
    ordered_by_name: string | null;
}

function mapRow(row: RawPendingOrderRow): PendingApproval {
    return {
        orderId: row.order_id,
        visitId: row.visit_id,
        facilityId: row.facility_id,
        facilityName: row.facility_name,
        physicianId: row.physician_id,
        residentId: row.resident_id,
        residentFirstName: row.resident_first_name ?? "",
        residentLastName: row.resident_last_name ?? "",
        roomNumber: row.room_number,
        orderType: row.order_type,
        description: row.description,
        details: row.details,
        priority: row.priority,
        dueDate: row.due_date,
        scheduledDate: row.scheduled_date,
        orderedAt: row.ordered_at,
        orderedBy: row.ordered_by,
        orderedByName: row.ordered_by_name,
    };
}

export async function listMyPendingApprovals(): Promise<PendingApproval[]> {
    const { data, error } = await supabase.rpc("list_my_pending_orders");
    if (error) {
        console.warn("[pending-approvals] list failed:", error.message);
        throw new Error(`Anordnungen konnten nicht geladen werden: ${error.message}`);
    }
    return ((data ?? []) as RawPendingOrderRow[]).map(mapRow);
}

function mapApproveError(message: string): string {
    if (message.includes("PIN_VERIFY_FAILED")) return "PIN ist falsch.";
    if (message.includes("PIN_VERIFY_LOCKED"))
        return "Zu viele Fehlversuche — kurz warten und erneut probieren.";
    if (message.includes("permission denied"))
        return "Keine Berechtigung fuer diese Anordnung.";
    return `Freigabe fehlgeschlagen: ${message}`;
}

async function verifyPin(physicianId: string, pin: string): Promise<void> {
    const { data, error } = await supabase.rpc("verify_physician_approval_pin", {
        p_physician_id: physicianId,
        p_pin: pin,
    });
    if (error) {
        throw new Error(mapApproveError(error.message));
    }
    if (data !== true) {
        throw new Error("PIN ist falsch oder gesperrt.");
    }
}

async function updateOrderApproved(orderId: string, physicianId: string): Promise<void> {
    const { data: userResp, error: userErr } = await supabase.auth.getUser();
    if (userErr || !userResp.user) {
        throw new Error("Sitzung abgelaufen — bitte erneut anmelden.");
    }

    const now = new Date().toISOString();
    const { data, error } = await supabase
        .from("visit_orders")
        .update({
            status: "ordered",
            approved_at: now,
            approved_by: userResp.user.id,
        })
        .eq("id", orderId)
        .eq("physician_id", physicianId)
        .eq("status", "pending")
        .select("id");

    if (error) {
        throw new Error(mapApproveError(error.message));
    }
    if (!data || data.length === 0) {
        throw new Error("Bereits freigegeben oder nicht zugeordnet.");
    }
}

export async function approveOrderWithPin(
    orderId: string,
    physicianId: string,
    pin: string,
): Promise<void> {
    await verifyPin(physicianId, pin);
    await updateOrderApproved(orderId, physicianId);
}

export interface BatchOrderRef {
    orderId: string;
    physicianId: string;
}

export async function approveBatch(
    orders: BatchOrderRef[],
    pin: string,
): Promise<BatchApproveResult> {
    if (orders.length === 0) {
        return { approved: [], failed: [] };
    }

    const distinctPhysicians = Array.from(new Set(orders.map((o) => o.physicianId)));
    for (const pid of distinctPhysicians) {
        await verifyPin(pid, pin);
    }

    const result: BatchApproveResult = { approved: [], failed: [] };
    for (const ref of orders) {
        try {
            await updateOrderApproved(ref.orderId, ref.physicianId);
            result.approved.push(ref.orderId);
        } catch (err) {
            result.failed.push({
                orderId: ref.orderId,
                error: err instanceof Error ? err.message : String(err),
            });
        }
    }
    return result;
}

export function subscribeToMyPendingOrders(onChange: () => void): RealtimeChannel {
    const channel = supabase
        .channel("pending-approvals:visit_orders")
        .on(
            "postgres_changes",
            { event: "*", schema: "public", table: "visit_orders" },
            () => onChange(),
        )
        .subscribe();
    return channel;
}
